import { fg } from '@atlaskit/platform-feature-flags';
import type { MentionMap } from '@atlassian/ai-model-io/utils/mention-map';

import type {
	UpdateIdMap,
	EditorPluginAIConfigItem,
} from '../../prebuilt/config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../prebuilt/config-items/utils/slice-or-node-to-markdown';
import { convoAIStatelessApiURL } from '../../prebuilt/utils';

import { getSupportedMarkdowns } from './get-supported-markdown-from-schema';
import type { AIExperienceMachineContextForRequest, RequestOptions } from './types';

type Options = {
	context: AIExperienceMachineContextForRequest;
	configItem: EditorPluginAIConfigItem;
	mentionMap?: MentionMap;
	updateIdMap: UpdateIdMap;
};

// Ignored via go/ees005
// eslint-disable-next-line require-unicode-regexp
const sanitizeUrl = (url: string) => url.replace(/[^\w\-/.:%?=]+/g, '');

/**
 * A function that will make the payload for the Convo AI API.
 *
 * @see https://hello.atlassian.net/wiki/spaces/EDAI/pages/3416730365/API+Specs+assistance-service
 *
 * @private
 * @deprecated The assistance service is planned to be shutdown and has been deprecated. We should be using the new ConvoAI
 * endpoint instead. This function should be removed after editor_ai_convo_ai_endpoint_202505 has rolled out.
 */
export const getRequestOptions4AssistanceService = ({
	context,
	configItem,
	mentionMap,
	updateIdMap,
}: Options): RequestOptions => {
	const { aiProvider, userInput, editorView, positions, intl, latestPromptResponse } = context;
	const { product } = aiProvider;
	const { experienceId, endpoint, agentId, lang } = configItem;
	const editorSchema = editorView.state.schema;
	const aiSessionId = context.analyticsContext?.aiSessionId;
	const idMap = {};

	// if there is no selection, we want to pass only the doc
	// if there is selection, we want to pass only the selection (if we pass doc and selection, the doc will be ignored by BE)
	let needDoc = context.isEmpty;
	let needSelection = !context.isEmpty;

	if (configItem.alwaysNoDocAndSelection) {
		// for Brainstorm, Jira Draft reply, don't send document (we have history feedback that LLM was using the document too much)
		needDoc = false;
		needSelection = false;
	} else if (configItem.alwaysNeedDoc) {
		// for Jira Improve description
		needDoc = true;
		needSelection = false;
	} else if (
		// for Free generate and interrogation
		configItem.canTogglePageKnowledge
	) {
		// For range selection and usePageKnowledge, we're sending both doc and selection
		needDoc = context.usePageKnowledge;
	}

	const doc = needDoc
		? sliceOrNodeToMarkdown({
				node: editorView.state.doc,
				editorView,
				convertTo: 'markdown-plus',
				existingIdMap: idMap,
				updateIdMap,
				mentionMap,
				aiSessionId,
			})
		: undefined;
	const docContent = doc?.markdown;
	const docIdMap = doc?.idMap;
	const docContentStatistics = doc?.contentStatistics;

	const selection = needSelection
		? sliceOrNodeToMarkdown({
				slice: editorView.state.doc.slice(positions[0], positions[1]),
				editorView,
				convertTo: 'markdown-plus',
				existingIdMap: docIdMap,
				updateIdMap,
				mentionMap,
				aiSessionId,
			})
		: undefined;

	const additionalProps = aiProvider.getAdditionalProps && aiProvider.getAdditionalProps?.();

	// for Jira Improve description
	const payload = configItem.getRequestPayload?.({
		aiSessionId,
		locale: intl.locale,
		isEmpty: context.isEmpty,
		doc: docContent,
		selection: selection?.markdown,
	});

	return {
		doc: docContent,
		selection: selection?.markdown,
		contentStatistics: docContentStatistics || selection?.contentStatistics,
		isConvoAI: true,
		experienceId: experienceId || 'editor',
		endpoint: endpoint || convoAIStatelessApiURL,
		payload: JSON.stringify(
			payload || {
				recipient_agent_named_id: agentId || 'editor_intent_agent',
				// userInput for Free generate, Free generate interrogation, Brainstorm
				agent_input_prompt: userInput || undefined,
				agent_input_context: {
					// This can be expanded later on to use other properties as document titles
					// but this fix was introduced specifically for issues with context in Jira
					// in EDF-2247
					document_title: additionalProps?.issue_summary,
					ai_session_id: aiSessionId,
					product,
					objectId: fg('platform_editor_ai_objectid_in_convo_ai') ? aiProvider.objectId : undefined,
					locale: intl.locale,
					content_url: sanitizeUrl(window.document.location.href),
					input_mime_type: 'text/markdown',
					output_mime_type: 'text/markdown',
					supported_markdown_nodes: getSupportedMarkdowns(editorSchema),
					writing_intent: configItem.intentSchemaId,
					draft_full_content: docContent || undefined,
					// latestPromptResponse for Free generate interrogation, Refine response
					draft_selected_content: latestPromptResponse || selection?.markdown || undefined,
					// for translate
					transformation_subtype: lang,
				},
			},
		),
	};
};
